import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, str, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { navigate } from '../router.js';
import { appDisplayNameForKey } from '../app-names.js';
import '../components/sso-card.js';
import '../components/sso-button.js';

/**
 * `<no-access-page>` — friendly landing for an authenticated user who tried to open an app they are
 * not entitled to. The backend's `/sso/authorize` (and `/go/{app}`) 302s here with `?app=<key>`
 * instead of returning a bare 403, so the user gets an explanation and a way back to the launcher.
 */
@customElement('no-access-page')
@localized()
export class NoAccessPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .center {
        text-align: center;
      }
      .badge {
        font-size: 2.5rem;
        text-align: center;
        color: var(--sso-color-muted);
      }
      .muted {
        color: var(--sso-color-muted);
      }
      sso-button {
        margin-top: var(--sso-space);
      }
    `,
  ];

  @state() private appName = '';

  override connectedCallback(): void {
    super.connectedCallback();
    const key = new URLSearchParams(location.search).get('app') ?? '';
    this.appName = appDisplayNameForKey(key);
  }

  override firstUpdated(): void {
    this.focusFirstControl();
  }

  override render() {
    return html`
      <sso-card>
        <div class="badge" aria-hidden="true">🔒</div>
        <h1 class="center">${msg('No access')}</h1>
        <p class="center">
          ${this.appName
            ? msg(str`Your account is not authorised to open ${this.appName}.`)
            : msg('Your account is not authorised to open this application.')}
        </p>
        <p class="center muted">
          ${msg('If you think this is a mistake, please contact your administrator.')}
        </p>
        <sso-button variant="primary" @click=${() => navigate('/')}>
          ${msg('Back to applications')}
        </sso-button>
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'no-access-page': NoAccessPage;
  }
}
